import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Input, Button, Table, Drawer, Select, Checkbox, message } from 'antd';
import { 
    getReportAccessListAPI, 
    getRoleReportAccessAPI, 
    updateReportAccessAPI,
    getActiveRolesAPI
} from 'services/system_setup/reportAccess';

const { Option } = Select;

// Table columns for landing page
const columns = [
    {
        title: 'ROLE NAME',
        dataIndex: 'role_name',
        key: 'role_name',
        width: '25%',
    },
    {
        title: 'REPORTS',
        dataIndex: 'reports',
        key: 'reports',
        width: '75%',
        render: (text) => {
            if (text === '*Unassigned*') {
                return <span style={{ color: '#ff4d4f' }}>{text}</span>;
            }
            if (text === 'ALL') {
                return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{text}</span>;
            }
            return text;
        }
    },
];

export default function ReportAccessManagerTab() {
    const [form] = Form.useForm();
    const [drawerForm] = Form.useForm();
    const [searchValue, setSearchValue] = useState('');
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roles, setRoles] = useState([]);
    const [reports, setReports] = useState([]);
    const [selectedReports, setSelectedReports] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);

    // Get userID from sessionStorage
    const loggedinUser = JSON.parse(sessionStorage.getItem('LOGGEDIN_USER_DATA'));
    const userID = loggedinUser && loggedinUser.userID;

    // Fetch landing page data
    const fetchListData = async (roleName = '') => {
        setLoading(true);
        try {
            const data = await getReportAccessListAPI(roleName);
            setTableData(data);
        } catch (error) {
            message.error('Failed to fetch report access list');
            console.error(error);
        }
        setLoading(false);
    };

    // Fetch active roles for dropdown
    const fetchRoles = async () => {
        try {
            const data = await getActiveRolesAPI();
            setRoles(data);
        } catch (error) {
            console.error('Failed to fetch roles:', error);
        }
    };

    // Initial load
    useEffect(() => {
        fetchListData();
        fetchRoles();
    }, []);

    // Handle search
    const handleSearch = () => {
        fetchListData(searchValue);
    };

    // Handle clear
    const handleClear = () => {
        setSearchValue('');
        form.resetFields();
        fetchListData();
    };

    // Open drawer when row is double-clicked
    const handleRowDoubleClick = async (record) => {
        setSelectedRole(record);
        drawerForm.setFieldsValue({ userRole: record.role_id });
        await fetchRoleReports(record.role_id);
        setOpen(true);
    };

    // Fetch report access for a specific role
    const fetchRoleReports = async (roleId) => {
        try {
            const data = await getRoleReportAccessAPI(roleId);
            setReports(data);
            // Set selected reports based on is_active
            const selected = data.filter(r => r.is_active).map(r => r.document_id);
            setSelectedReports(selected);
        } catch (error) {
            message.error('Failed to fetch role reports');
            console.error(error);
        }
    };

    // Handle role change in drawer dropdown
    const handleRoleChange = async (roleId) => {
        const role = roles.find(r => r.role_id === roleId);
        setSelectedRole(role);
        await fetchRoleReports(roleId);
    };

    // Handle checkbox change
    const handleCheckboxChange = (documentId, checked) => {
        if (checked) {
            setSelectedReports([...selectedReports, documentId]);
        } else {
            setSelectedReports(selectedReports.filter(id => id !== documentId));
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!selectedRole) {
            message.warning('Please select a user role');
            return;
        }

        setSaveLoading(true);
        try {
            await updateReportAccessAPI(selectedRole.role_id, selectedReports, userID);
            message.success('Report access updated successfully');
            setOpen(false);
            fetchListData(searchValue);
        } catch (error) {
            message.error('Failed to update report access');
            console.error(error);
        }
        setSaveLoading(false);
    };

    // Handle drawer close
    const onClose = () => {
        setOpen(false);
        setSelectedRole(null);
        setReports([]);
        setSelectedReports([]);
        drawerForm.resetFields();
    };

    return (
        <div>
            <Form
                form={form}
                layout="inline"
                onFinish={handleSearch}
                style={{ marginBottom: 16 }}
            >
                <Row style={{ width: '100%' }} gutter={8} align="middle">
                    <Col>
                        <Form.Item name="roleName" style={{ marginBottom: 0 }}>
                            <Input
                                placeholder="ROLE NAME"
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                                style={{ width: 200 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col>
                        <Button type="primary" htmlType="submit">
                            Search
                        </Button>
                    </Col>
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button onClick={handleClear}>
                            Clear
                        </Button>
                    </Col>
                </Row>
            </Form>

            <Table
                style={{ textTransform: 'uppercase' }}
                columns={columns}
                dataSource={tableData}
                loading={loading}
                bordered
                pagination={{ defaultPageSize: 5 }}
                rowKey="role_id"
                onRow={(record) => ({
                    onDoubleClick: () => handleRowDoubleClick(record)
                })}
            />

            <Drawer
                title="Update Access"
                placement="right"
                onClose={onClose}
                visible={open}
                width={400}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={onClose} style={{ marginRight: 8 }}>
                            Back
                        </Button>
                        <Button type="primary" onClick={handleSave} loading={saveLoading}>
                            Save
                        </Button>
                    </div>
                }
            >
                <Form form={drawerForm} layout="vertical">
                    <Form.Item 
                        label="User Role" 
                        name="userRole"
                        rules={[{ required: true, message: 'Please select a user role' }]}
                    >
                        <Select
                            placeholder="Please select"
                            onChange={handleRoleChange}
                            showSearch
                            optionFilterProp="children"
                            style={{ width: '100%' }}
                        >
                            {roles.map(role => (
                                <Option key={role.role_id} value={role.role_id}>
                                    {role.role_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ marginTop: 16 }}>
                        <Row style={{ fontWeight: 'bold', marginBottom: 8, borderBottom: '1px solid #d9d9d9', paddingBottom: 8 }}>
                            <Col span={6}>Select</Col>
                            <Col span={18}>Report Name</Col>
                        </Row>
                        {reports.map(report => (
                            <Row 
                                key={report.document_id} 
                                style={{ 
                                    padding: '8px 0', 
                                    borderBottom: '1px solid #f0f0f0' 
                                }}
                            >
                                <Col span={6}>
                                    <Checkbox
                                        checked={selectedReports.includes(report.document_id)}
                                        onChange={(e) => handleCheckboxChange(report.document_id, e.target.checked)}
                                    />
                                </Col>
                                <Col span={18}>
                                    {report.document_name}
                                </Col>
                            </Row>
                        ))}
                    </div>
                </Form>
            </Drawer>
        </div>
    );
}
